/* eslint-disable sonarjs/cognitive-complexity */
import {
  INDICATOR,
  INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS,
  INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE,
} from '@/api/rest/resources/types/IndicatorProduct';
import { WidgetType } from '@/api/rest/resources/types/widget';
import { useGetIndicatorProductSubscriptionConfigurations } from '@/hooks/useDashboardIndicatorProductSubscriptionConfiguration';
import { useSelectedIndicatorProducts } from '@/pages/shared/indicators/hooks/useIndicatorProducts';

import { useIsStaffUser } from './useIsStaffUser';

export const APPROVED_DEFAULT_FILTERS = {
  status: INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS.APPROVED,
  type: INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE.DEFAULT,
} as const;

export const useInsituIndicatorConfigurations = (filters: Record<string, string> = APPROVED_DEFAULT_FILTERS) => {
  const orgIndicatorProducts = useSelectedIndicatorProducts();
  const hasInsituDataIp = orgIndicatorProducts.some((ip) => ip.name === INDICATOR.IN_SITU_PACK);

  const { data } = useGetIndicatorProductSubscriptionConfigurations(undefined, true, filters);

  if (!hasInsituDataIp) {
    return [];
  }

  return data?.results ?? [];
};

const useInSituIndicatorConfigurationWidgets = (
  status?: (typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS)[keyof typeof INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS],
) => {
  const filters = status
    ? { status, type: INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE.DEFAULT }
    : APPROVED_DEFAULT_FILTERS;

  const configurations = useInsituIndicatorConfigurations(filters);
  const widgetFormats = configurations.map((config) => config?.widget?.widget_format).filter(Boolean);

  return widgetFormats.flatMap((widgetFormat) => {
    if (Array.isArray(widgetFormat)) {
      return widgetFormat;
    }
    return widgetFormat ? [widgetFormat] : [];
  });
};

export const useInSituIndicatorConfigurationPlotWidgets = () => {
  const inSituWidgets = useInSituIndicatorConfigurationWidgets();
  const pendingApprovalInSituWidgets = useInSituPendingApprovalIndicatorConfigurationWidgets({
    widgetType: WidgetType.o_plot_card,
  });
  const pendingApprovalInSituWidgetsWithoutWidgets = useInSituPendingApprovalIndicatorConfigurationWithoutWidgets({
    widgetType: WidgetType.o_plot_card,
  });
  return [
    ...inSituWidgets.filter((widget) => widget?.type === WidgetType.o_plot_card),
    ...pendingApprovalInSituWidgets,
    ...pendingApprovalInSituWidgetsWithoutWidgets,
  ];
};

export const useInSituIndicatorConfigurationOrgWidgetsByDomain = (domain: string) => {
  const inSituWidgets = useInSituIndicatorConfigurationWidgets();
  const orgInSituWidgets = inSituWidgets.filter((widget) => widget?.type === WidgetType.o_org_card);
  const pendingApprovalInSituWidgets = useInSituPendingApprovalIndicatorConfigurationWidgets({
    widgetType: WidgetType.o_org_card,
  });
  const pendingApprovalInSituWidgetsWithoutWidgets = useInSituPendingApprovalIndicatorConfigurationWithoutWidgets({
    widgetType: WidgetType.o_org_card,
    domain,
  });

  if (!domain) {
    return [];
  }

  const insituWidgets = orgInSituWidgets.filter((widget) => widget?.relatedData?.domain === domain);
  return [...insituWidgets, ...pendingApprovalInSituWidgets, ...pendingApprovalInSituWidgetsWithoutWidgets];
};

export const useInSituPendingApprovalIndicatorConfigurationWidgets = ({ widgetType }: { widgetType: WidgetType }) => {
  const isStaff = useIsStaffUser();
  const inSituWidgets = useInSituIndicatorConfigurationWidgets(
    INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS.VALIDATED,
  );
  if (!isStaff) {
    return [];
  }

  return inSituWidgets
    .filter((widget) => widget?.type === widgetType)
    .map((widget) => ({
      ...widget,
      relatedData: {
        ...widget?.relatedData,
        widgetConfiguration: {
          name: `${widget?.relatedData?.widgetConfiguration?.name}-pending-approval`,
          type: WidgetType.a_pending_approval_frame,
          widgets: [widget?.relatedData?.widgetConfiguration],
          visible: true,
        },
      },
    }));
};

export const useInSituPendingApprovalIndicatorConfigurationWithoutWidgets = ({
  widgetType,
  domain,
}: {
  widgetType?: WidgetType;
  domain?: string;
}) => {
  const isStaff = useIsStaffUser();

  const validatedConfigurations = useInsituIndicatorConfigurations({
    status: INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS.VALIDATED,
    type: INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_TYPE.DEFAULT,
  });
  const configsWithMissingWidgets = validatedConfigurations.filter((config) => {
    if (!config?.widget) {
      return true;
    }

    if (!widgetType && !domain) {
      return false;
    }

    const widgetFormat = config?.widget?.widget_format;
    if (!widgetFormat) {
      return true;
    }

    const formattedWidgetFormat = Array.isArray(widgetFormat) ? widgetFormat : [widgetFormat];
    return formattedWidgetFormat.every((widget) => {
      const missesTypeCheck = widget?.type !== widgetType;
      if (!domain) {
        return missesTypeCheck;
      }
      const missesDomainCheck = widget?.relatedData?.domain !== domain;

      if (!widgetType) {
        return missesDomainCheck;
      }

      return missesDomainCheck || missesTypeCheck;
    });
  });

  if (!isStaff) {
    return [];
  }

  return configsWithMissingWidgets.map((c) => ({
    name: `${c?.id}-no-widget`,
    type: WidgetType.a_pending_widget,
    visible: true,
    relatedData: {
      insituIndicatorProductConfiguration: c,
      domain,
    },
  }));
};
